import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Trash2, FileDown, Eye, Undo2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { usePendingClients, usePendingEngagements } from '@/api/hooks';
import { api, ApiError } from '@/api/client';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';
import { submitErrorMessage } from '@/lib/formErrors';
import { encodeEngId } from '@/lib/engId';
import { fmtDateTime, clientSubId } from '@/lib/format';

const STATUS = ['', 'Yes', 'No', 'N/A'];
// Mandatory statuses (Client Manager / Return Manager): no blank option, default 'No'.
const REQ_STATUS = ['Yes', 'No', 'N/A'];
// A request is "being completed" when its statuses are all Yes/N/A (Power Apps rule).
// The stored proc then writes CompletionDate, so a date is required for those rows.
const DONE = ['Yes', 'N/A'];
const DATE_MSG = 'Please enter the completion date to submit the form.';

type Row = Record<string, unknown>;
// Item 35: engagements on the client, shown in the delete confirmation.
interface DeleteCheck { recordType: string; engagements: number; pendingEngagementIds: string[] }

// Submit = complete the whole grid; delC/delE = delete one request (Power Apps
// confirms deletes before calling the proc).
type Confirm =
  | { kind: 'submit'; grid: 'c' | 'e' }
  | { kind: 'delC'; row: Row; check?: DeleteCheck }
  | { kind: 'delE'; row: Row };

// Per-tab memory (edits, scroll position) so leaving the page and coming back keeps your place.
const store = {
  get<T>(k: string, d: T): T {
    try { const v = sessionStorage.getItem(k); return v ? (JSON.parse(v) as T) : d; } catch { return d; }
  },
  set(k: string, v: unknown) {
    try { sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* convenience only */ }
  },
};

const text = (v: unknown) => String(v ?? '').trim();
const rowLabel = (r: Row) => String(r.ClientName || `${r.ClientID ?? ''}${r.ClientSubID ? '/' + r.ClientSubID : ''}`);
const plural = (k: number, w: string) => `${k} ${w}${k === 1 ? '' : 's'}`;

/** A read-only column; `sort` makes its header clickable. */
interface InfoCol { label: string; sort?: (r: Row) => string; cell: (r: Row) => ReactNode }
interface StatusCol { key: string; label: string; required?: boolean }
interface GridDef {
  id: 'c' | 'e';
  title: string;
  noun: string;
  keyOf: (r: Row) => string;
  lead: InfoCol[];
  statuses: StatusCol[];
  /** Statuses that must be Yes/N/A for the row to complete. */
  doneKeys: string[];
  searchText: (r: Row) => string;
  searchHint: string;
}

const typeCol = (label: string): InfoCol => ({ label, sort: (r) => text(r.RecordType), cell: (r) => text(r.RecordType) });
const submittedCol: InfoCol = { label: 'Submitted', sort: (r) => text(r.CreatedDate), cell: (r) => fmtDateTime(r.CreatedDate) };
const byCol = (label: string): InfoCol => ({ label, sort: (r) => text(r.CreatedBy), cell: (r) => text(r.CreatedBy) });
const byColFor = (def: GridDef): InfoCol => byCol(def.id === 'c' ? 'Submitted By' : 'By');

const CLIENTS: GridDef = {
  id: 'c',
  title: 'Client Requests',
  noun: 'client request',
  keyOf: (r) => `${r.ClientID ?? ''}|${r.ClientSubID ?? ''}`,
  lead: [
    typeCol('Request Type'),
    { label: 'Client/Sub ID', sort: (r) => clientSubId(r), cell: (r) => clientSubId(r) },
    { label: 'Client Name', sort: (r) => text(r.ClientName), cell: (r) => text(r.ClientName) },
    submittedCol,
  ],
  statuses: [
    { key: 'MaconomyStatus', label: 'Maconomy' },
    { key: 'ConstantStatus', label: 'Constant' },
    { key: 'ClientManagerStatus', label: 'Client Manager', required: true },
    { key: 'ReturnManagerStatus', label: 'Return Manager', required: true },
  ],
  doneKeys: ['MaconomyStatus', 'ConstantStatus'],
  searchText: (r) => [r.ClientID, r.ClientSubID, r.ClientName, r.RecordType, r.CreatedBy].map(text).join(' '),
  searchHint: 'Client name / ID, type or submitter…',
};

const ENGAGEMENTS: GridDef = {
  id: 'e',
  title: 'Engagement Requests',
  noun: 'engagement request',
  keyOf: (r) => text(r.EngagementID),
  lead: [
    typeCol('Type'),
    {
      label: 'Client',
      sort: (r) => text(r.ClientName),
      cell: (r) => (
        <div className="min-w-[9rem]">
          <div className="text-foreground">{text(r.ClientName)}</div>
          <div className="text-xs text-muted-foreground">{text(r.ClientID)}</div>
        </div>
      ),
    },
    // Engagement ID + name, so rows for the same client (e.g. a mass change) can be told apart.
    {
      label: 'Engagement',
      sort: (r) => text(r.EngagementID),
      cell: (r) => (
        <div className="min-w-[9rem]">
          <div className="font-medium text-foreground">{text(r.EngagementID)}</div>
          <div className="text-xs text-muted-foreground">{text(r.EngagementName) || [text(r.TemplateName), text(r.Year)].filter(Boolean).join(' ')}</div>
        </div>
      ),
    },
    submittedCol,
  ],
  statuses: [
    { key: 'MaconomyStatus', label: 'Maconomy' },
    { key: 'AxcessClientProfileStatus', label: 'Client Profile' },
    { key: 'ReturnManagerStatus', label: 'Return Mgr' },
    { key: 'AxcessWorkflowStatus', label: 'Workflow' },
    { key: 'CCHEngagementStatus', label: 'CCH Eng' },
    { key: 'EngagementLetterStatus', label: 'EL' },
  ],
  doneKeys: ['MaconomyStatus', 'AxcessClientProfileStatus', 'ReturnManagerStatus', 'AxcessWorkflowStatus', 'CCHEngagementStatus', 'EngagementLetterStatus'],
  searchText: (r) => [r.ClientID, r.ClientName, r.EngagementID, r.EngagementName, r.TemplateName, r.RecordType, r.CreatedBy].map(text).join(' '),
  searchHint: 'Client, engagement or submitter…',
};

/**
 * State for one grid: inline edits (kept per browser tab), filters and sort (kept in the URL),
 * row selection for bulk set, and the scroll position.
 */
function useGrid(def: GridDef, query: UseQueryResult<Row[]>) {
  const data = query.data;
  const rows = useMemo(() => data ?? [], [data]);
  const [params, setParams] = useSearchParams();
  const param = (k: string) => params.get(def.id + k) ?? '';
  const setParam = (k: string, v: string) =>
    setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(def.id + k, v); else n.delete(def.id + k); return n; }, { replace: true });
  const search = param('q');
  const type = param('type');
  const by = param('by');
  const sort = param('sort');

  const editsKey = `pending.edits.${def.id}`;
  const [edits, setEdits] = useState<Record<string, Row>>(() => store.get(editsKey, {}));
  useEffect(() => { store.set(editsKey, edits); }, [editsKey, edits]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Drop edits / selections for requests that are no longer pending (completed or deleted).
  useEffect(() => {
    if (!data) return;
    const keys = new Set(data.map(def.keyOf));
    setEdits((s) => {
      const kept = Object.entries(s).filter(([k]) => keys.has(k));
      return kept.length === Object.keys(s).length ? s : Object.fromEntries(kept);
    });
    setSelected((s) => {
      const kept = [...s].filter((k) => keys.has(k));
      return kept.length === s.size ? s : new Set(kept);
    });
  }, [data, def]);

  const val = (r: Row, k: string, fallback = '') => (edits[def.keyOf(r)]?.[k] ?? r[k] ?? fallback) as string;
  const setVal = (r: Row, k: string, v: string) => {
    const key = def.keyOf(r);
    setEdits((s) => ({ ...s, [key]: { ...s[key], [k]: v } }));
  };
  const statusOf = (r: Row, c: StatusCol) => val(r, c.key) || (c.required ? 'No' : '');
  const isDone = (r: Row) => def.doneKeys.every((k) => DONE.includes(val(r, k)));
  const needsDate = (r: Row) => isDone(r) && !val(r, 'CompletionDate');

  const types = useMemo(() => [...new Set(rows.map((r) => text(r.RecordType)).filter(Boolean))].sort(), [rows]);
  const people = useMemo(() => [...new Set(rows.map((r) => text(r.CreatedBy)).filter(Boolean))].sort(), [rows]);
  const shown = useMemo(() => {
    const cols = [...def.lead, byColFor(def)];
    const q = search.trim().toLowerCase();
    let out = rows.filter((r) =>
      (!type || text(r.RecordType) === type) &&
      (!by || text(r.CreatedBy) === by) &&
      (!q || def.searchText(r).toLowerCase().includes(q)));
    const [label, dir] = sort.split(':');
    const col = cols.find((c) => c.label === label && c.sort);
    if (col?.sort) {
      const get = col.sort;
      out = [...out].sort((a, b) => get(a).localeCompare(get(b), undefined, { numeric: true }) * (dir === 'desc' ? -1 : 1));
    }
    return out;
  }, [rows, search, type, by, sort, def]);

  // Header click: ascending -> descending -> back to the default (newest first).
  const toggleSort = (label: string) => {
    const [cur, dir] = sort.split(':');
    setParam('sort', cur !== label ? `${label}:asc` : dir === 'asc' ? `${label}:desc` : '');
  };

  // Restore the scroll position once rows are on screen.
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollKey = `pending.scroll.${def.id}`;
  const restored = useRef(false);
  useLayoutEffect(() => {
    if (restored.current || !data || !scrollRef.current) return;
    restored.current = true;
    scrollRef.current.scrollTop = store.get(scrollKey, 0);
  }, [data, scrollKey]);

  return {
    def, query, rows, shown, edits, setEdits, selected, setSelected, val, setVal, statusOf, isDone, needsDate,
    search, type, by, sort, setParam, toggleSort, types, people, scrollRef, scrollKey,
    filtered: !!(search || type || by),
    dirty: Object.keys(edits).length > 0,
    blocked: rows.some(needsDate),
  };
}
type Grid = ReturnType<typeof useGrid>;

export function Pending() {
  const notify = useToast();
  const qc = useQueryClient();
  const c = useGrid(CLIENTS, usePendingClients() as UseQueryResult<Row[]>);
  const e = useGrid(ENGAGEMENTS, usePendingEngagements() as UseQueryResult<Row[]>);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  // Submit sends every pending row (not just the filtered ones), like before.
  async function submitClients() {
    const missing = c.rows.filter(c.needsDate).map(rowLabel);
    if (missing.length) { notify('error', `${DATE_MSG} Missing for: ${missing.join(', ')}.`); return; }
    const rows = c.rows.map((r) => ({
      clientId: String(r.ClientID ?? ''),
      clientSubId: String(r.ClientSubID ?? ''),
      maconomyStatus: c.val(r, 'MaconomyStatus'),
      constantStatus: c.val(r, 'ConstantStatus'),
      clientManagerStatus: c.val(r, 'ClientManagerStatus') || 'No',
      returnManagerStatus: c.val(r, 'ReturnManagerStatus') || 'No',
      dateCompleted: c.val(r, 'CompletionDate'),
    }));
    await submit(c, '/pending/clients/submit', rows, 'Client requests submitted.');
  }

  async function submitEngagements() {
    const missing = e.rows.filter(e.needsDate).map(rowLabel);
    if (missing.length) { notify('error', `${DATE_MSG} Missing for: ${missing.join(', ')}.`); return; }
    const rows = e.rows.map((r) => ({
      engagementId: String(r.EngagementID ?? ''),
      maconomyStatus: e.val(r, 'MaconomyStatus'),
      axcessClientProfileStatus: e.val(r, 'AxcessClientProfileStatus'),
      returnManagerStatus: e.val(r, 'ReturnManagerStatus'),
      axcessWorkflowStatus: e.val(r, 'AxcessWorkflowStatus'),
      cchEngagementStatus: e.val(r, 'CCHEngagementStatus'),
      engagementLetterStatus: e.val(r, 'EngagementLetterStatus'),
      dateCompleted: e.val(r, 'CompletionDate'),
    }));
    await submit(e, '/pending/engagements/submit', rows, 'Engagement requests submitted.');
  }

  // Submit completions; if the import step reports a warning, show it (the
  // completion still succeeded and the data moved to Completed Requests).
  async function submit(g: Grid, url: string, rows: unknown[], ok: string) {
    try {
      const res = await api.post<{ importWarning?: string }>(url, { rows });
      g.setEdits({});
      g.setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['pending'] });
      qc.invalidateQueries({ queryKey: ['counts'] });
      notify(res?.importWarning ? 'info' : 'success', res?.importWarning ?? ok);
    } catch (err) {
      notify('error', submitErrorMessage(err));
    }
  }

  async function run(fn: () => Promise<unknown>, ok: string) {
    try {
      await fn();
      notify('success', ok);
      qc.invalidateQueries({ queryKey: ['pending'] });
      qc.invalidateQueries({ queryKey: ['counts'] });
    } catch (err) {
      notify('error', err instanceof ApiError ? err.problem.title : 'Action failed');
    }
  }

  const clientPath = (r: Row) =>
    `/pending/clients/${r.ClientID}/${r.ClientSubID}`;
  const clientTypeQs = (r: Row) => `clientType=${encodeURIComponent(String(r.ClientType ?? 'Master'))}`;
  // Look up the client's engagements first so the confirmation can say what the delete means.
  async function askDeleteClient(r: Row) {
    let check: DeleteCheck | undefined;
    try {
      check = await api.get<DeleteCheck>(`${clientPath(r)}/delete-check?${clientTypeQs(r)}`);
    } catch {
      check = undefined; // fall back to the plain confirmation
    }
    setConfirm({ kind: 'delC', row: r, check });
  }
  async function deleteClient(r: Row) {
    await run(() => api.del(`${clientPath(r)}?${clientTypeQs(r)}&confirm=true`), 'Client request deleted.');
  }
  async function deleteEngagement(r: Row) {
    await run(() => api.del(`/pending/engagements/${r.EngagementID}`), 'Engagement request deleted.');
  }

  let confirmMsg = '';
  if (confirm?.kind === 'submit') {
    const g = confirm.grid === 'c' ? c : e;
    const done = g.rows.filter(g.isDone).length;
    confirmMsg = `Submit all ${plural(g.rows.length, g.def.noun)}? `
      + (done
        ? `${done} marked Yes/N/A with a date will move to Completed Requests. The rest stay in Pending with the statuses shown.`
        : 'None are marked complete, so all stay in Pending with the statuses shown.');
  }
  if (confirm && (confirm.kind === 'delC' || confirm.kind === 'delE')) {
    const what = confirm.kind === 'delC' ? 'client' : 'engagement';
    confirmMsg = `Delete the ${what} request for ${rowLabel(confirm.row)}? This cannot be undone.`;
  }
  if (confirm?.kind === 'delC' && confirm.check) {
    const { recordType, engagements: n, pendingEngagementIds: ids } = confirm.check;
    const label = rowLabel(confirm.row);
    if (recordType === 'New Client' && ids.length > 0) {
      confirmMsg = `${plural(ids.length, 'pending engagement request')} (${ids.join(', ')}) ${ids.length === 1 ? 'is' : 'are'} for this new client. `
        + `If you delete the client request for ${label}, ${ids.length === 1 ? 'that engagement request' : 'those engagement requests'} will have no client. `
        + 'Delete the client request anyway? This cannot be undone.';
    } else if (n > 0) {
      confirmMsg = `${label} has ${plural(n, 'engagement')}. Deleting this request only throws away the requested changes. `
        + 'The client keeps its current details and its engagements are not touched. Delete this request?';
    }
  }

  return (
    // Two grids share the screen height and each scrolls on its own.
    <div className="flex h-full flex-col gap-6 p-6">
      <div className="flex flex-shrink-0 items-center justify-between">
        <h1 className="text-lg font-semibold text-foreground">Pending Requests</h1>
        <Link to="/pending/download" className="btn-secondary inline-flex items-center gap-1">
          <FileDown className="h-4 w-4" /> Download Pending Requests
        </Link>
      </div>

      <PendingGrid g={c} onSubmit={() => setConfirm({ kind: 'submit', grid: 'c' })}
        actions={(r) => (
          <>
            <Link
              to={`/clients/${encodeURIComponent(String(r.ClientID ?? ''))}/${encodeURIComponent(String(r.ClientSubID ?? '0'))}`}
              title="View Details" className="text-muted-foreground hover:text-primary">
              <Eye className="h-4 w-4" />
            </Link>
            <button className="text-destructive hover:opacity-70" title="Delete Client Request"
              onClick={() => void askDeleteClient(r)}><Trash2 className="h-4 w-4" /></button>
          </>
        )} />

      <PendingGrid g={e} onSubmit={() => setConfirm({ kind: 'submit', grid: 'e' })}
        actions={(r) => (
          <>
            <Link to={`/engagements/${encodeEngId(String(r.EngagementID ?? ''))}`}
              title="View Details" className="text-muted-foreground hover:text-primary">
              <Eye className="h-4 w-4" />
            </Link>
            <button className="text-destructive hover:opacity-70" title="Delete Engagement Request"
              onClick={() => setConfirm({ kind: 'delE', row: r })}><Trash2 className="h-4 w-4" /></button>
          </>
        )} />

      <ConfirmDialog
        open={confirm !== null}
        message={confirmMsg}
        onConfirm={() => {
          const cf = confirm;
          if (!cf) return;
          setConfirm(null);
          if (cf.kind === 'submit') void (cf.grid === 'c' ? submitClients() : submitEngagements());
          else if (cf.kind === 'delC') void deleteClient(cf.row);
          else void deleteEngagement(cf.row);
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

const StatusSel = ({ value, onChange, options = STATUS }: { value: string; onChange: (v: string) => void; options?: string[] }) => (
  <select className="input w-[4.75rem] px-2" value={value} onChange={(ev) => onChange(ev.target.value)}>
    {options.map((s) => <option key={s} value={s}>{s || '—'}</option>)}
  </select>
);

function PendingGrid({ g, onSubmit, actions }: { g: Grid; onSubmit: () => void; actions: (r: Row) => ReactNode }) {
  const notify = useToast();
  const { def, query, rows, shown } = g;
  const [sortLabel, sortDir] = g.sort.split(':');
  const picked = shown.filter((r) => g.selected.has(def.keyOf(r)));
  const allPicked = shown.length > 0 && picked.length === shown.length;
  const colCount = def.lead.length + def.statuses.length + 5;

  // Bulk set: '' = leave that status as it is.
  const [bulk, setBulk] = useState<Record<string, string>>({});
  function applyBulk() {
    const vals = Object.fromEntries(Object.entries(bulk).filter(([, v]) => v));
    if (!Object.keys(vals).length) { notify('error', 'Pick at least one status or a date to set.'); return; }
    g.setEdits((s) => {
      const n = { ...s };
      for (const r of picked) { const k = def.keyOf(r); n[k] = { ...n[k], ...vals }; }
      return n;
    });
    notify('success', `Set on ${plural(picked.length, 'row')}. Review them, then click Submit to save.`);
    setBulk({});
  }
  const toggle = (r: Row) => g.setSelected((s) => {
    const n = new Set(s); const k = def.keyOf(r);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const toggleAll = () => g.setSelected((s) => {
    const n = new Set(s);
    for (const r of shown) { if (allPicked) n.delete(def.keyOf(r)); else n.add(def.keyOf(r)); }
    return n;
  });

  const Head = ({ col }: { col: InfoCol }) => (
    <th className="table-cell text-left">
      {col.sort ? (
        <button type="button" className="inline-flex items-center gap-1 uppercase hover:text-foreground" onClick={() => g.toggleSort(col.label)}
          title="Sort">
          {col.label}
          {sortLabel === col.label
            ? (sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)
            : <ArrowUpDown className="h-3 w-3 opacity-40" />}
        </button>
      ) : col.label}
    </th>
  );
  const submittedBy = byColFor(def);

  return (
    <section className="flex min-h-[280px] flex-1 flex-col">
      <div className="mb-2 flex flex-shrink-0 items-center justify-between">
        <h2 className="text-base font-semibold text-foreground">
          {def.title} <span className="text-sm font-normal text-muted-foreground">({rows.length})</span>
        </h2>
        <div className="flex gap-2">
          <button className="btn-secondary inline-flex items-center gap-1" disabled={!g.dirty}
            title="Discard unsaved status / date edits"
            onClick={() => { g.setEdits({}); void query.refetch(); }}>
            <Undo2 className="h-4 w-4" /> Discard Changes
          </button>
          <button className="btn-success" disabled={g.blocked || rows.length === 0} title={g.blocked ? DATE_MSG : ''}
            onClick={onSubmit}>Submit</button>
        </div>
      </div>

      {/* Filters: kept in the URL, so coming back from View Details returns to the same view. */}
      <div className="mb-2 flex flex-shrink-0 flex-wrap items-center gap-2">
        <input className="input w-72" placeholder={def.searchHint} value={g.search}
          onChange={(ev) => g.setParam('q', ev.target.value)} />
        <select className="input w-48" value={g.type} onChange={(ev) => g.setParam('type', ev.target.value)}>
          <option value="">All request types</option>
          {g.types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="input w-56" value={g.by} onChange={(ev) => g.setParam('by', ev.target.value)}>
          <option value="">Anyone</option>
          {g.people.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        {g.filtered && (
          <button className="btn-secondary" type="button"
            onClick={() => { g.setParam('q', ''); g.setParam('type', ''); g.setParam('by', ''); }}>Clear filters</button>
        )}
        <span className="text-xs text-muted-foreground">
          {g.filtered ? `Showing ${shown.length} of ${rows.length}` : `${plural(rows.length, 'request')}`}
        </span>
        {/* Bulk set is hidden until rows are ticked, so say it is there. */}
        {picked.length === 0 && shown.length > 1 && (
          <span className="text-xs text-muted-foreground">
            Tip: tick the boxes on the left (or the top box for all shown) to set statuses and the date on several requests at once.
          </span>
        )}
      </div>

      {/* Bulk set: shown once rows are ticked. Values are applied as unsaved edits; Submit saves them. */}
      {picked.length > 0 && (
        <div className="mb-2 flex flex-shrink-0 flex-wrap items-end gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm">
          <span className="self-center font-medium text-foreground">{picked.length} selected. Set:</span>
          {def.statuses.map((s) => (
            <div key={s.key}>
              <label className="label">{s.label}</label>
              <select className="input w-24 px-2" value={bulk[s.key] ?? ''} onChange={(ev) => setBulk((b) => ({ ...b, [s.key]: ev.target.value }))}>
                <option value="">Keep</option>
                {REQ_STATUS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          ))}
          <div>
            <label className="label">Date Completed</label>
            <input type="date" className="input w-[8.5rem] px-2" value={bulk.CompletionDate ?? ''}
              onChange={(ev) => setBulk((b) => ({ ...b, CompletionDate: ev.target.value }))} />
          </div>
          <button className="btn-primary" type="button" onClick={applyBulk}>Apply to {picked.length}</button>
          <button className="btn-secondary" type="button" onClick={() => g.setSelected(new Set())}>Clear selection</button>
        </div>
      )}

      <div ref={g.scrollRef} onScroll={(ev) => store.set(g.scrollKey, ev.currentTarget.scrollTop)}
        className="min-h-0 flex-1 overflow-auto rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full divide-y divide-border text-sm">
          <thead className="table-head text-xs uppercase text-muted-foreground">
            <tr>
              <th className="table-cell w-8">
                <input type="checkbox" aria-label="Select all shown" checked={allPicked} disabled={!shown.length} onChange={toggleAll} />
              </th>
              {def.lead.map((col) => <Head key={col.label} col={col} />)}
              {def.statuses.map((s) => <th key={s.key} className="table-cell text-left">{s.label}</th>)}
              <th className="table-cell text-left">Date Completed</th>
              <Head col={submittedBy} />
              <th className="table-cell" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {shown.map((r) => {
              const key = def.keyOf(r);
              return (
                <tr key={key} className={g.edits[key] ? 'bg-warning/10' : undefined}>
                  <td className="table-cell">
                    <input type="checkbox" aria-label="Select row" checked={g.selected.has(key)} onChange={() => toggle(r)} />
                  </td>
                  {def.lead.map((col) => <td key={col.label} className="table-cell">{col.cell(r)}</td>)}
                  {def.statuses.map((s) => (
                    <td key={s.key} className="table-cell">
                      <StatusSel options={s.required ? REQ_STATUS : STATUS} value={g.statusOf(r, s)} onChange={(v) => g.setVal(r, s.key, v)} />
                    </td>
                  ))}
                  <td className="table-cell"><div className="flex items-center gap-1">
                    <input type="date" className="input w-[8.5rem] px-2" value={g.val(r, 'CompletionDate')}
                      onChange={(ev) => g.setVal(r, 'CompletionDate', ev.target.value)} />
                    {g.needsDate(r) && <span title={DATE_MSG} className="font-bold text-orange-500">!</span>}
                  </div></td>
                  <td className="table-cell">{submittedBy.cell(r)}</td>
                  <td className="table-cell">
                    <div className="flex items-center justify-end gap-2">{actions(r)}</div>
                  </td>
                </tr>
              );
            })}
            {query.isFetching && <tr><td className="table-cell" colSpan={colCount}>Loading…</td></tr>}
            {!query.isFetching && rows.length === 0 && <tr><td className="table-cell text-muted-foreground" colSpan={colCount}>No pending {def.noun}s.</td></tr>}
            {!query.isFetching && rows.length > 0 && shown.length === 0 && (
              <tr><td className="table-cell text-muted-foreground" colSpan={colCount}>No requests match the filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
